/**
 * Role cards — left column of the Roles tab (mockup "Role list" section).
 * Owner always first with a 🔒 Locked pill; the selected card gets the ruby
 * border; "+ New role" opens the editor with an empty, unsaved draft.
 */
import { ADMIN_SECTIONS, OWNER_ROLE_ID, type RolePermissions } from '@platform/shared-types';
import type { StaffRole } from '../../../services/staffApi';
import { countLevels } from './roleMath';

const TOTAL_SECTIONS = ADMIN_SECTIONS.length;

function summarize(permissions: RolePermissions) {
  return countLevels(permissions || {});
}

export function RoleList({ roles, selectedId, onSelect, onNew }: {
  roles: StaffRole[];
  selectedId: string | null;
  onSelect: (role: StaffRole) => void;
  onNew: () => void;
}) {
  // Owner first, then everything else in the order the server returned it.
  const ordered = [...roles].sort((a, b) => {
    if (a.id === OWNER_ROLE_ID) return -1;
    if (b.id === OWNER_ROLE_ID) return 1;
    return 0;
  });

  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={onNew}
        className="w-full min-h-[44px] rounded-xl border-2 border-dashed border-stone-300 text-stone-600 hover:border-[#DC143C] hover:text-[#DC143C] text-sm font-medium"
      >
        + New role
      </button>

      {ordered.map((role) => {
        const isOwnerRole = role.id === OWNER_ROLE_ID;
        const counts = summarize(role.permissions || {});
        const selected = role.id === selectedId;
        return (
          <button
            key={role.id}
            type="button"
            onClick={() => onSelect(role)}
            className={`w-full text-left bg-white rounded-xl p-3 ${selected ? 'border-2 border-[#DC143C] shadow-sm' : 'border border-stone-200'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-stone-900">{role.name}</p>
              {isOwnerRole ? (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-stone-900 text-white shrink-0">🔒 Locked</span>
              ) : selected ? (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FFF1F3] text-[#9F1239] shrink-0">Editing</span>
              ) : null}
            </div>
            {role.description && <p className="text-xs text-stone-500 mt-0.5">{role.description}</p>}
            {isOwnerRole ? (
              <p className="text-xs text-stone-600 mt-2">{role.staffCount ?? 1} member{(role.staffCount ?? 1) === 1 ? '' : 's'} · All sections</p>
            ) : (
              <>
                <div className="flex gap-1.5 mt-2 text-[11px]">
                  {counts.edit > 0 && <span className="px-1.5 py-0.5 rounded bg-[#DC143C] text-white">{counts.edit} Edit</span>}
                  {counts.view > 0 && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">{counts.view} View</span>}
                  {counts.off > 0 && <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{counts.off} Off</span>}
                </div>
                <p className="text-xs text-stone-600 mt-2">
                  {role.staffCount ?? 0} member{(role.staffCount ?? 0) === 1 ? '' : 's'}
                  {counts.off === TOTAL_SECTIONS ? '' : ` · ${counts.edit} Edit · ${counts.view} View`}
                </p>
              </>
            )}
          </button>
        );
      })}
    </section>
  );
}

export default RoleList;
